import { useState } from 'react';
import { Alert, Button, Card, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import type { TicketDetail } from './data';
import { useAction } from './useAction';

const SCORES = [1, 2, 3, 4, 5] as const;

/** Encuesta de satisfacción (guía 7.1): el solicitante la responde una vez, cuando el ticket queda resuelto. */
export function SurveyCard({ ticket }: { ticket: TicketDetail }) {
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const action = useAction();

  if (ticket.csat !== null) {
    return (
      <Card title={t('tk.encuesta')}>
        {action.notice && <Alert tone="success">{action.notice}</Alert>}
        <p className="text-sm">
          <Stars value={ticket.csat} /> <span className="ml-1 text-slate-600">{t('tk.encuesta_valor', { n: ticket.csat })}</span>
        </p>
        {ticket.csatComment && <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">“{ticket.csatComment}”</p>}
      </Card>
    );
  }
  if (!ticket.can.rate) return null;

  return (
    <Card title={t('tk.encuesta')}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">{t('tk.encuesta_pregunta')}</p>
        {action.error && <Alert>{action.error}</Alert>}
        <div role="radiogroup" aria-label={t('tk.encuesta_calificacion')} className="flex gap-1">
          {SCORES.map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={score === n}
              aria-label={t(`tk.encuesta_${n}` as 'tk.encuesta_1')}
              title={t(`tk.encuesta_${n}` as 'tk.encuesta_1')}
              className={`text-2xl leading-none ${score !== null && n <= score ? 'text-amber-500' : 'text-slate-300 hover:text-amber-300'}`}
              onClick={() => setScore(n)}
            >
              ★
            </button>
          ))}
        </div>
        {score !== null && <p className="text-xs text-slate-600">{t(`tk.encuesta_${score}` as 'tk.encuesta_1')}</p>}
        <Textarea id="tk-encuesta-comentario" label={t('tk.encuesta_comentario')} rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
        <Button
          disabled={action.busy || score === null}
          onClick={() => void action.run(() => api(`/api/tickets/${ticket.number}/encuesta`, { json: { score, comment: comment.trim() || null } }), t('tk.encuesta_gracias'))}
        >
          {t('tk.encuesta_enviar')}
        </Button>
      </div>
    </Card>
  );
}

function Stars({ value }: { value: number }) {
  return (
    <span aria-hidden className="text-amber-500">
      {'★'.repeat(value)}
      <span className="text-slate-300">{'★'.repeat(5 - value)}</span>
    </span>
  );
}
